import React, { useState, useRef, useCallback } from 'react';
import { Card, Row, Col, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileImport, 
  faUpload, 
  faCheckCircle, 
  faExclamationTriangle, 
  faInfo,
  faDownload,
  faFileExcel,
  faFileCsv,
  faRobot,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import FieldMappingModal from './FieldMappingModal';
import ImportProgressTracker from './ImportProgressTracker';
import FileStructurePreview from './FileStructurePreview';
import ImportHistory from './ImportHistory';

interface LeadImportsProps {
  onComplete?: () => void;
}

const LeadImports: React.FC<LeadImportsProps> = ({ onComplete }) => {
  const [sourceSystemName, setSourceSystemName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [uploadMessage, setUploadMessage] = useState('');
  const [processingStats, setProcessingStats] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // New state for enhanced features
  const [fileStructure, setFileStructure] = useState<any>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string>('');
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, string>>({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [showProgress, setShowProgress] = useState(false);
  const [refreshHistory, setRefreshHistory] = useState(0);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
        setUploadStatus('error');
        setUploadMessage('Please select a CSV or Excel file');
        return;
      }
      
      // Validate file size (max 100MB for agent processing)
      if (file.size > 100 * 1024 * 1024) {
        setUploadStatus('error');
        setUploadMessage('File size must be less than 100MB');
        return;
      }

      setSelectedFile(file);
      setUploadStatus('idle');
      setUploadMessage('');
      
      // For Excel files, analyze structure
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        await analyzeExcelFile(file);
      }
    }
  };

  const analyzeExcelFile = async (file: File) => {
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_system', sourceSystemName);
      
      const response = await axiosInstance.post('/contact-import/analyze-fields', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setFileStructure(response.data.structure);
      setAiSuggestions(response.data.suggestions || {});
      setShowMappingModal(true);
    } catch (error) {
      console.error('File analysis error:', error);
      setUploadStatus('error');
      setUploadMessage('Failed to analyze file structure');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const analyzeCSVFile = async (file: File) => {
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_system', sourceSystemName);
      
      const response = await axiosInstance.post('/contact-import/analyze-fields', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setFileStructure(response.data.structure);
      setAiSuggestions(response.data.suggestions || {});
      setShowMappingModal(true);
    } catch (error) {
      console.error('CSV analysis error:', error);
      setUploadStatus('error');
      setUploadMessage('Failed to analyze CSV structure');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFieldMappingConfirm = (mappings: Record<string, string>) => {
    console.log('🎯 [FIELD MAPPING] Confirmed mappings:', mappings);
    setFieldMappings(mappings);
    setShowMappingModal(false);
    // Small delay to ensure state is updated before upload
    setTimeout(() => {
      console.log('📤 [UPLOAD] Starting upload with mappings:', mappings);
      handleUpload();
    }, 100);
  };

  const handleUpload = async () => {
    if (!selectedFile || !sourceSystemName.trim()) {
      setUploadStatus('error');
      setUploadMessage('Please select a file and specify the source system');
      return;
    }

    setIsUploading(true);
    setUploadStatus('idle');
    setShowProgress(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('source_system_name', sourceSystemName.trim());
      formData.append('enable_ai_evaluation', 'true');
      formData.append('enable_llm_scoring', 'true');
      
      // Add field mappings for both CSV and Excel files
      if (Object.keys(fieldMappings).length > 0) {
        formData.append('field_mappings', JSON.stringify(fieldMappings));
        console.log('🗺️ [FRONTEND] Sending field mappings:', fieldMappings);
      }
      
      // Add selected sheet for Excel files
      if (selectedFile.name.toLowerCase().endsWith('.xlsx') && selectedSheet) {
        formData.append('selected_sheet', selectedSheet);
      }

      const response = await axiosInstance.post('/contact-import/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.job_id) {
        // For large files processed by agent
        setCurrentJobId(response.data.job_id);
        setIsUploading(false);
      } else if (response.data.success) {
        // For small files processed immediately
        setUploadStatus('success');
        setUploadMessage(`Successfully imported ${response.data.leads_created || 0} leads`);
        setProcessingStats(response.data);
        setShowProgress(false);
        
        // Save completion state
        localStorage.setItem('leadImportsComplete', 'true');
        
        // Reset form
        resetForm();
        
        // Refresh history
        setRefreshHistory(prev => prev + 1);
        
        // Notify parent component
        if (onComplete) {
          onComplete();
        }
      } else {
        setUploadStatus('error');
        setUploadMessage(response.data.message || 'Upload failed');
        setShowProgress(false);
      }
    } catch (error: any) {
      console.error('Lead import error:', error);
      setUploadStatus('error');
      setUploadMessage(error.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      if (!currentJobId) {
        setIsUploading(false);
      }
    }
  };

  const handleImportComplete = (result: any) => {
    setUploadStatus('success');
    setUploadMessage(`Successfully imported ${result.leads_created || 0} leads`);
    setProcessingStats(result);
    setShowProgress(false);
    
    // Save completion state
    localStorage.setItem('leadImportsComplete', 'true');
    
    // Reset form
    resetForm();
    
    // Refresh history
    setRefreshHistory(prev => prev + 1);
    
    // Notify parent component
    if (onComplete) {
      onComplete();
    }
  };

  const handleImportError = (error: string) => {
    setUploadStatus('error');
    setUploadMessage(error);
    setShowProgress(false);
    setIsUploading(false);
  };

  const resetForm = () => {
    setSelectedFile(null);
    setSourceSystemName('');
    setFileStructure(null);
    setFieldMappings({});
    setCurrentJobId('');
    setAiSuggestions({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadSampleTemplate = () => {
    // Create a universal CSV template that works with most CRM systems
    const headers = [
      'first_name', 'last_name', 'email', 'phone', 'address', 'city', 'state', 'zip_code',
      'lead_source', 'lead_stage', 'tags', 'notes', 'created_date'
    ];
    
    const sampleRows = [
      'John,Doe,john.doe@example.com,555-123-4567,123 Main St,Phoenix,AZ,85001,Website,Active,Buyer,Looking for 3BR home,2025-01-01',
      'Jane,Smith,jane.smith@example.com,555-987-6543,456 Oak Ave,Scottsdale,AZ,85251,Referral,New,Seller,Want to sell townhouse,2025-01-02'
    ];
    
    const csvContent = headers.join(',') + '\n' + sampleRows.join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'lead_import_template.csv';
    link.click();
  };

  return (
    <div>
      <Row className="gy-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-gradient-primary text-white">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faFileImport} className="me-2" />
                <h5 className="mb-0">Lead Imports</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {/* Source System Input */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold mb-3">
                      <FontAwesomeIcon icon={faInfo} className="me-2 text-info" />
                      Source System Information
                    </Form.Label>
                    <Row>
                      <Col md={8}>
                        <Form.Control
                          type="text"
                          placeholder="Enter the name of your CRM or lead source (e.g., CRM System, Lead Database, etc.)"
                          value={sourceSystemName}
                          onChange={(e) => setSourceSystemName(e.target.value)}
                          className="mb-3"
                        />
                        <Form.Text className="text-muted">
                          Enter the name of your CRM system, lead database, or export source. This helps our AI system better understand your data format.
                        </Form.Text>
                      </Col>
                      <Col md={4} className="text-center">
                        <Card className="border-info bg-light h-100">
                          <Card.Body className="d-flex flex-column justify-content-center">
                            <FontAwesomeIcon icon={faDownload} className="text-info mb-2" size="2x" />
                            <Button 
                              variant="outline-info" 
                              size="sm"
                              onClick={downloadSampleTemplate}
                            >
                              <FontAwesomeIcon icon={faDownload} className="me-1" />
                              Download Sample Template
                            </Button>
                            <small className="text-muted mt-2">
                              Universal CSV format
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
              </Row>

              {/* File Upload Section */}
              {sourceSystemName.trim() && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <Card className="border-2 border-dashed border-primary bg-light">
                      <Card.Body className="text-center p-4">
                        <FontAwesomeIcon 
                          icon={selectedFile ? faCheckCircle : faUpload} 
                          size="3x" 
                          className={`mb-3 ${selectedFile ? 'text-success' : 'text-muted'}`} 
                        />
                        
                        {!selectedFile ? (
                          <>
                            <h6 className="mb-2">Upload Your {sourceSystemName} CSV File</h6>
                            <p className="text-muted mb-3">
                              Drag your CSV file here or click to browse
                            </p>
                            <Form.Control
                              ref={fileInputRef}
                              type="file"
                              accept=".csv,.xlsx,.xls"
                              onChange={handleFileSelect}
                              className="d-none"
                            />
                            <Button 
                              variant="primary" 
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isUploading || isAnalyzing}
                            >
                              <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                              Choose File
                            </Button>
                            <div className="mt-3">
                              <Badge bg="info">
                                <FontAwesomeIcon icon={faRobot} className="me-1" />
                                AI-Powered Field Mapping Available for Excel Files
                              </Badge>
                            </div>
                          </>
                        ) : (
                          <>
                            <h6 className="mb-2 text-success">File Selected</h6>
                            <p className="mb-2">
                              <FontAwesomeIcon icon={faFileCsv} className="me-2" />
                              {selectedFile.name}
                            </p>
                            <p className="text-muted small mb-3">
                              Size: {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                            <div className="d-flex gap-2 justify-content-center">
                              <Button 
                                variant="success" 
                                onClick={() => {
                                  if ((selectedFile?.name.toLowerCase().endsWith('.xlsx') || selectedFile?.name.toLowerCase().endsWith('.xls')) && !fileStructure) {
                                    analyzeExcelFile(selectedFile);
                                  } else {
                                    handleUpload();
                                  }
                                }}
                                disabled={isUploading || isAnalyzing}
                              >
                                {isAnalyzing ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Analyzing...
                                  </>
                                ) : isUploading ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Processing...
                                  </>
                                ) : (selectedFile?.name.toLowerCase().endsWith('.xlsx') || selectedFile?.name.toLowerCase().endsWith('.xls')) && !fileStructure ? (
                                  <>
                                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                    Analyze & Map Fields
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                                    Import Leads
                                  </>
                                )}
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                onClick={() => {
                                  setSelectedFile(null);
                                  if (fileInputRef.current) {
                                    fileInputRef.current.value = '';
                                  }
                                }}
                                disabled={isUploading}
                              >
                                Cancel
                              </Button>
                            </div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Status Messages */}
              {uploadStatus !== 'idle' && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <Alert 
                      variant={uploadStatus === 'success' ? 'success' : 'danger'}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon 
                        icon={uploadStatus === 'success' ? faCheckCircle : faExclamationTriangle} 
                        className="me-2" 
                      />
                      {uploadMessage}
                    </Alert>
                  </Col>
                </Row>
              )}

              {/* Processing Stats */}
              {processingStats && uploadStatus === 'success' && (
                <Row>
                  <Col xs={12}>
                    <Card className="border-success">
                      <Card.Header className="bg-success text-white">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        Import Summary
                      </Card.Header>
                      <Card.Body>
                        <Row className="text-center">
                          <Col md={3}>
                            <div className="border-end">
                              <h4 className="text-primary mb-1">{processingStats.total_records || 0}</h4>
                              <small className="text-muted">Total Records</small>
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="border-end">
                              <h4 className="text-success mb-1">{processingStats.leads_created || 0}</h4>
                              <small className="text-muted">Leads Created</small>
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="border-end">
                              <h4 className="text-info mb-1">{processingStats.ai_evaluations || 0}</h4>
                              <small className="text-muted">AI Evaluations</small>
                            </div>
                          </Col>
                          <Col md={3}>
                            <h4 className="text-warning mb-1">{processingStats.llm_scoring_scheduled || 0}</h4>
                            <small className="text-muted">LLM Scoring Scheduled</small>
                          </Col>
                        </Row>
                        {processingStats.processing_time && (
                          <div className="text-center mt-3">
                            <small className="text-muted">
                              Processing completed in {processingStats.processing_time}
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Information Panel */}
              <Row className="mt-4">
                <Col xs={12}>
                  <Alert variant="info">
                    <FontAwesomeIcon icon={faInfo} className="me-2" />
                    <strong>Automated Processing:</strong> Once uploaded, your leads will be:
                    <ul className="mb-0 mt-2">
                      <li>Automatically imported and deduplicated</li>
                      <li>Enhanced with AI-powered lead intelligence</li>
                      <li>Scored using our LLM-powered scoring system</li>
                      <li>Added to your workflow for follow-up automation</li>
                    </ul>
                  </Alert>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Field Mapping Modal */}
      {fileStructure && (
        <FieldMappingModal
          show={showMappingModal}
          onHide={() => setShowMappingModal(false)}
          detectedColumns={fileStructure.sheets[0]?.columns || []}
          sourceSystem={sourceSystemName}
          aiSuggestions={aiSuggestions}
          onConfirmMapping={handleFieldMappingConfirm}
          sampleData={fileStructure.sheets[0]?.sampleData || []}
        />
      )}

      {/* File Structure Preview */}
      {fileStructure && !showMappingModal && !showProgress && (
        <Row className="mt-4">
          <Col xs={12}>
            <FileStructurePreview
              fileStructure={fileStructure}
              onSheetSelect={setSelectedSheet}
            />
          </Col>
        </Row>
      )}

      {/* Progress Tracker */}
      {showProgress && currentJobId && (
        <Row className="mt-4">
          <Col xs={12}>
            <ImportProgressTracker
              jobId={currentJobId}
              onComplete={handleImportComplete}
              onError={handleImportError}
            />
          </Col>
        </Row>
      )}

      {/* Import History */}
      <Row className="mt-4">
        <Col xs={12}>
          <ImportHistory refreshTrigger={refreshHistory} />
        </Col>
      </Row>
    </div>
  );
};

export default LeadImports;